/* eslint-disable jsx-a11y/label-has-associated-control, jsx-a11y/control-has-associated-label */
/* eslint-disable no-underscore-dangle */
import { useState } from 'react';
import PropTypes from 'prop-types';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { scheduleDailyPuzzle, updateDailyPuzzle, deleteDailyPuzzle } from '../../services/adminApi';

// Helper to format date as YYYY-MM-DD using local date components
const formatLocalDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

function ManageSchedulesModal({
  image, onClose, onScheduleChange, onError,
}) {
  const [scheduleMode, setScheduleMode] = useState(null); // 'add' | 'edit'
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [scheduleFormDate, setScheduleFormDate] = useState(null);
  const [scheduleFormRows, setScheduleFormRows] = useState(4);
  const [scheduleFormCols, setScheduleFormCols] = useState(4);
  const [scheduleFormPieceStyle, setScheduleFormPieceStyle] = useState('classic');
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);

  const handleStartAddSchedule = () => {
    setScheduleMode('add');
    setEditingSchedule(null);
    setScheduleFormDate(null);
    setScheduleFormRows(image.defaultConfig?.rows || 4);
    setScheduleFormCols(image.defaultConfig?.columns || 4);
    setScheduleFormPieceStyle(image.defaultConfig?.pieceStyle || 'classic');
  };

  const handleStartEditSchedule = (schedule) => {
    setScheduleMode('edit');
    setEditingSchedule(schedule);
    setScheduleFormDate(new Date(schedule.date));
    setScheduleFormRows(schedule.config?.rows || 4);
    setScheduleFormCols(schedule.config?.columns || 4);
    setScheduleFormPieceStyle(schedule.config?.pieceStyle || 'classic');
  };

  const handleCancelScheduleForm = () => {
    setScheduleMode(null);
    setEditingSchedule(null);
    setScheduleFormDate(null);
  };

  const handleSaveSchedule = async () => {
    if (!image || !scheduleFormDate) return;

    try {
      setIsSavingSchedule(true);
      const dateStr = formatLocalDate(scheduleFormDate);

      if (scheduleMode === 'add') {
        await scheduleDailyPuzzle(dateStr, image._id, {
          rows: scheduleFormRows,
          columns: scheduleFormCols,
          pieceStyle: scheduleFormPieceStyle,
        });
      } else if (scheduleMode === 'edit' && editingSchedule) {
        await updateDailyPuzzle(editingSchedule._id, {
          date: dateStr,
          rows: scheduleFormRows,
          columns: scheduleFormCols,
          pieceStyle: scheduleFormPieceStyle,
        });
      }

      setScheduleMode(null);
      setEditingSchedule(null);
      setScheduleFormDate(null);
      onScheduleChange();
    } catch (err) {
      onError(err.message);
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Remove this scheduled date?')) return;

    try {
      await deleteDailyPuzzle(scheduleId);
      onScheduleChange();
    } catch (err) {
      onError(err.message);
    }
  };

  const handleClose = () => {
    setScheduleMode(null);
    setEditingSchedule(null);
    setScheduleFormDate(null);
    onClose();
  };

  if (!image) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-puzzle-surface rounded-lg max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-puzzle-border flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-puzzle-text">Manage Schedules</h2>
            <p className="text-sm text-puzzle-text-secondary mt-1">{image.title}</p>
          </div>
          <button
            type="button"
            onClick={handleClose}
            className="text-puzzle-text-secondary hover:text-puzzle-text"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* List of existing schedules */}
          {!scheduleMode && (
            <>
              <h3 className="text-sm font-medium text-puzzle-text mb-3">Scheduled Dates</h3>
              {image.scheduledDates && image.scheduledDates.length > 0 ? (
                <div className="space-y-2 mb-4">
                  {image.scheduledDates
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .map((schedule) => (
                      <div
                        key={schedule._id}
                        className="flex items-center justify-between p-3 bg-puzzle-muted rounded-lg"
                      >
                        <div>
                          <span className="font-medium text-puzzle-text">
                            {new Date(schedule.date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                          </span>
                          <span className="text-sm text-puzzle-text-secondary ml-2">
                            {schedule.config?.rows || 4}
                            ×
                            {schedule.config?.columns || 4}
                            {' '}
                            {schedule.config?.pieceStyle || 'classic'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => handleStartEditSchedule(schedule)}
                            className="px-2 py-1 text-xs bg-puzzle-blue/10 text-puzzle-blue rounded hover:bg-puzzle-blue/20"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSchedule(schedule._id)}
                            className="px-2 py-1 text-xs bg-puzzle-danger/10 text-puzzle-danger rounded hover:bg-puzzle-danger/20"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="text-sm text-puzzle-text-secondary mb-4">No schedules yet.</p>
              )}

              <button
                type="button"
                onClick={handleStartAddSchedule}
                className="w-full py-2 border-2 border-dashed border-puzzle-border rounded-lg text-puzzle-text-secondary hover:border-puzzle-blue hover:text-puzzle-blue transition-colors"
              >
                + Add New Schedule
              </button>
            </>
          )}

          {/* Add/Edit form */}
          {scheduleMode && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-puzzle-text">
                {scheduleMode === 'add' ? 'Add New Schedule' : 'Edit Schedule'}
              </h3>

              {/* Date picker */}
              <div>
                <label className="block text-sm font-medium text-puzzle-text mb-2">
                  Date
                </label>
                <Calendar
                  onChange={setScheduleFormDate}
                  value={scheduleFormDate}
                  minDate={new Date()}
                  className="mx-auto"
                />
              </div>

              {/* Grid size */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="scheduleRows" className="block text-sm font-medium text-puzzle-text mb-1">
                    Rows
                  </label>
                  <select
                    id="scheduleRows"
                    value={scheduleFormRows}
                    onChange={(e) => setScheduleFormRows(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue bg-puzzle-surface text-puzzle-text"
                  >
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="scheduleCols" className="block text-sm font-medium text-puzzle-text mb-1">
                    Columns
                  </label>
                  <select
                    id="scheduleCols"
                    value={scheduleFormCols}
                    onChange={(e) => setScheduleFormCols(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue bg-puzzle-surface text-puzzle-text"
                  >
                    {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Piece style */}
              <div>
                <label htmlFor="schedulePieceStyle" className="block text-sm font-medium text-puzzle-text mb-1">
                  Piece Style
                </label>
                <select
                  id="schedulePieceStyle"
                  value={scheduleFormPieceStyle}
                  onChange={(e) => setScheduleFormPieceStyle(e.target.value)}
                  className="w-full px-3 py-2 border border-puzzle-border rounded-lg focus:ring-2 focus:ring-puzzle-blue bg-puzzle-surface text-puzzle-text"
                >
                  <option value="classic">Classic</option>
                  <option value="traditional">Traditional</option>
                  <option value="rounded">Rounded</option>
                  <option value="wavy">Wavy</option>
                  <option value="square">Square</option>
                  <option value="random">Random</option>
                </select>
              </div>

              {/* Preview */}
              {scheduleFormDate && (
                <p className="text-sm text-puzzle-success text-center">
                  {scheduleFormRows}
                  ×
                  {scheduleFormCols}
                  {' '}
                  {scheduleFormPieceStyle}
                  {' puzzle on '}
                  {scheduleFormDate.toLocaleDateString()}
                </p>
              )}

              {/* Form buttons */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={handleCancelScheduleForm}
                  className="px-4 py-2 text-puzzle-text hover:bg-puzzle-hover rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveSchedule}
                  disabled={isSavingSchedule || !scheduleFormDate}
                  className="px-4 py-2 bg-puzzle-blue text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isSavingSchedule ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

ManageSchedulesModal.propTypes = {
  image: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    defaultConfig: PropTypes.shape({
      rows: PropTypes.number,
      columns: PropTypes.number,
      pieceStyle: PropTypes.string,
    }),
    scheduledDates: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      config: PropTypes.shape({
        rows: PropTypes.number,
        columns: PropTypes.number,
        pieceStyle: PropTypes.string,
      }),
    })),
  }),
  onClose: PropTypes.func.isRequired,
  onScheduleChange: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
};

ManageSchedulesModal.defaultProps = {
  image: null,
};

export default ManageSchedulesModal;
